import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';

import {connectAppEnv} from '../../../../app/app-env';
import {translateProps, frefFromRef, frefToRef} from '../../../../lib/react/higher-order-components';
import {withAddedUILevel} from '../../../../lib/react/oe-higher-order-components';
import {oeInterfaceManager} from '../../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../../react-oe/oe-interface-adapter';
import {OEFeatureAccessTypes, OELicensingEdition} from '../../../../react-oe/oe-types';
import retardUpdate from '../../../../lib/react/update-retarder';
import {OETarget} from '../../../../react-oe/oe-target';
import OEIconCodes from '../../../../lib/oe-icon-codes';
import {OEFNStartSelectionContentFactory} from '../../../views/flow-nav/start-selection/oe-fn-start-selection-model';
import OEStdCapBarSelector from './oe-std-cap-bar-selector';
import {OEMainMenuButtonsPropType} from '../../main-menu/oe-main-menu-model';
import {OEStdCapBarTargetTypeFlags, OEStdCapBarPropTypes} from './oe-std-cap-bar-model';
import OEToolbox from '../../../../lib/tools/oe-toolbox';

export class OEStdCapBarController extends React.PureComponent {

    constructor(props) {
        super(props);

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);
        this.connected = false;

        this.licensingAccess = this.getLicensingAccess();

        this.targetTypes = this.getTargetTypes();
        this.activeTargetType = this.getActiveTargetType();

        this.state = {
            targetTypes:        clone(this.targetTypes),
            activeTargetType:   this.activeTargetType
        };

        this.updateLastTargetForType();

        this.updateLanguage = this.updateLanguage.bind(this);
        this.onLicensingProductActivationChanged = this.onLicensingProductActivationChanged.bind(this);

        this.onTargetBtnPressed = this.onTargetBtnPressed.bind(this);
        this.onTargetBtnDisabledPressed = this.onTargetBtnDisabledPressed.bind(this);
    }

    componentWillUpdate(nextProps)   {
        let targetTypesChanged = !OEToolbox.shallowEqual(nextProps.targetTypes, this.props.targetTypes, true);

        if(targetTypesChanged)  this.updateTargetTypes(nextProps);

        if(nextProps.target !== this.props.target || nextProps.targetTypeForTarget !== this.props.targetTypeForTarget || targetTypesChanged)  {
            let needsLastTargetForTypeReset = nextProps.targetTypeForTarget !== this.props.targetTypeForTarget;
            if(!needsLastTargetForTypeReset)    {
                let next = nextProps.targetTypes ? nextProps.targetTypes.map(targetType => { return {id: targetType.id, target: targetType.target}; }) : null;
                let current = this.props.targetTypes ? this.props.targetTypes.map(targetType => { return {id: targetType.id, target: targetType.target}; }) : null;
                needsLastTargetForTypeReset = !OEToolbox.shallowEqual(next, current, true);
            }
            
            if(needsLastTargetForTypeReset)  this.lastTargetForType = {};

            this.updateActiveTargetType(nextProps);
            this.updateLastTargetForType(nextProps);
        }

        if(!targetTypesChanged && nextProps.isTargetTypeDisabled !== this.props.isTargetTypeDisabled)   this.updateTargetTypesEnabledState(nextProps);
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.licensingProductActivationChanged, this.onLicensingProductActivationChanged);
    }

    onRelease()    {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.licensingProductActivationChanged, this.onLicensingProductActivationChanged);
    }

    getActiveTargetType(props)    {
        props = props || this.props;
        if(props.targetTypeForTarget)   return props.targetTypeForTarget(props.target, this);

        if(!this.targetTypes.length)    return '';
        for(let i = 0; i < this.targetTypes.length; ++i)    if(this.targetTypes[i].target === props.target) return this.targetTypes[i].id;
        return this.targetTypes[0].id;
    }

    isTargetTypeDisabled(targetTypeId, props)    {
        props = props || this.props;

        if(props.targetTypes)   {
            let targetType = props.targetTypes.find(targetType => targetType.id === targetTypeId);
            if(targetType)  {
                let flags = typeof(targetType.flags) === 'number' ? targetType.flags : OEStdCapBarTargetTypeFlags.default;
                if(!this.connected && (flags & OEStdCapBarTargetTypeFlags.disableUnconnected))  return true;

                if(targetType.disabled)   return true;
            }
        }

        if(props.isTargetTypeDisabled)   return props.isTargetTypeDisabled(targetTypeId, this, Object.assign({}, this.licensingAccess, {oe: this.oe}));
        return false;
    }

    suppressDisabledTooltip(targetTypeId, props)    {
        props = props || this.props;

        if(props.targetTypes)   {
            let targetType = props.targetTypes.find(targetType => targetType.id === targetTypeId);
            if(targetType)  {
                let flags = typeof(targetType.flags) === 'number' ? targetType.flags : OEStdCapBarTargetTypeFlags.default;
                if(!this.connected && (flags & OEStdCapBarTargetTypeFlags.disableUnconnected))  return true;
            }
        }

        return false;
    }

    getTargetTypes(props)   {
        props = props || this.props;
        return !props.targetTypes ? [] : props.targetTypes.map(targetType => { return {
            id:                 targetType.id,
            disabled:           this.isTargetTypeDisabled(targetType.id, props),
            proto:              clone(targetType),
            strings:            this.getStringsForTargetType(targetType),
            suppressDisabledTooltip: this.suppressDisabledTooltip(targetType.id, props)
        }});
    }

    getStringsForTargetType(targetType)    {
        let ret = {
            label:              typeof(targetType.label) === 'string' ? targetType.label : (targetType.labelId ? '' : undefined),
            title:              targetType.title,
            tooltip:            targetType.tooltip,
            disabledTooltip:    targetType.disabledTooltip,
        };
        if(!this.oe.isReady())  return ret;
        const si = this.oe.sharedInterface;

        if(targetType.labelId)              ret.label = si.getLocalizedStringEnc(targetType.labelId);
        if(targetType.titleId)              ret.title = si.getLocalizedStringEnc(targetType.titleId);
        if(targetType.tooltipId)            ret.tooltip = si.getLocalizedStringEnc(targetType.tooltipId);
        if(targetType.disabledTooltipId)    ret.disabledTooltip = si.getLocalizedStringEnc(targetType.disabledTooltipId);
        if(!ret.tooltip)                    ret.tooltip = ret.title;
        return ret;
    }

    getLastTargetForType(targetType)    {
        targetType = targetType || this.activeTargetType;
        return this.lastTargetForType[targetType];
    }

    getLicensingAccess(released) {
        let isConnected = !released && this.oe.isReady();
        let isLicensing = config.accessType == OEFeatureAccessTypes.licensing && (!isConnected || this.oe.sharedInterface.getUIControllerFeatureAccess().isAccessType(this.oe.Module.FeatureAccessType.licensing));

        return {
            isConnected: isConnected,
            isLicensing: isLicensing,
            isValid: isLicensing ? (isConnected ? this.oe.sharedInterface.getUIControllerLicensingManager().isValid() : false) : true,
            edition: isLicensing ? (isConnected ? this.oe.sharedInterface.getUIControllerLicensingManager().getEdition() : OELicensingEdition.none) : OELicensingEdition.master
        };
    }

    updateTargetTypes(props)   {
        this.targetTypes = this.getTargetTypes(props);
        this.setState({targetTypes: clone(this.targetTypes)});
    }

    updateActiveTargetType(props)   {
        let activeTargetType = this.getActiveTargetType(props);
        if(activeTargetType === this.activeTargetType)  return;
        this.activeTargetType = activeTargetType;
        this.setState({activeTargetType: activeTargetType});
        this.onActiveTargetTypeChanged(props);
    }

    updateTargetTypesEnabledState(props) {
        this.targetTypes.forEach(targetType => {
            targetType.disabled = this.isTargetTypeDisabled(targetType.id, props);
            targetType.suppressDisabledTooltip = this.suppressDisabledTooltip(targetType.id, props)
        });
        this.setState({targetTypes: clone(this.targetTypes)});
    }

    updateTargetTypeStrings()   {
        this.targetTypes.forEach(targetType => {
            targetType.strings = this.getStringsForTargetType(targetType.proto);
        });
        this.setState({targetTypes: clone(this.targetTypes)});
    }

    updateLastTargetForType(props)   {
        props = props || this.props;
        if(!this.lastTargetForType) this.lastTargetForType = {};
        if(typeof(this.activeTargetType) !== 'string') return;
        this.lastTargetForType[this.activeTargetType] = props.target;
    }

    updateLicensingAccess(released) {
        let licensingAccess = this.getLicensingAccess(released);
        if(OEToolbox.shallowEqual(this.licensingAccess, licensingAccess))   return;
        this.licensingAccess = licensingAccess;
        this.onLicensingAccessChanged();
    }

    updateLanguage()   {
        this.updateTargetTypeStrings();
    }

    updateState(released)   {
        if(!this.oe.isReady() || released === true)   {
            this.connected = false;
            this.updateTargetTypesEnabledState();
            this.updateLicensingAccess(released);
            return;
        }

        this.connected = true;
        
        retardUpdate(this, () => {
            this.updateLanguage();
            this.updateLicensingAccess();
            this.updateTargetTypesEnabledState();
        });
    }

    onLicensingAccessChanged()   {
        this.updateTargetTypesEnabledState();
    }

    onLicensingProductActivationChanged()   {
        this.updateLicensingAccess();
    }

    onActiveTargetTypeChanged(props) {
        this.updateLastTargetForType(props);
    }

    render()    {
        let targetBarTitle = null;
        let targetBarButton;
        let targetMenuEntries;
        let targetMenuDisabled;
        let targetMenuHidden;
        let onTargetMenuDisabledToggle;

        let targetTypeBar = this.state.targetTypes.filter(targetType => !targetType.proto.noTargetTypeBarEntry).map((targetType) => {
            if(targetType.id === this.state.activeTargetType)   {
                targetBarTitle = targetType.strings.title;
                targetBarButton = targetType.proto.targetBarButton;
                targetMenuEntries = targetType.proto.targetMenuEntries;
                targetMenuDisabled = targetType.proto.targetMenuDisabled;
                targetMenuHidden = targetType.proto.targetMenuHidden;
                onTargetMenuDisabledToggle = targetType.proto.onTargetMenuDisabledToggle;
            }
            
            return {
                icon: targetType.proto.icon,
                label: targetType.strings.label,
                activated: targetType.id === this.state.activeTargetType,
                disabled: targetType.disabled,
                onPressed: this.onTargetBtnPressed,
                onDisabledPressed: this.onTargetBtnDisabledPressed,
                userData: targetType.proto,
                tooltip: !(targetType.proto.flags & OEStdCapBarTargetTypeFlags.noTooltip) ? targetType.strings.tooltip : undefined,
                disabledTooltip: !(targetType.proto.flags & OEStdCapBarTargetTypeFlags.noTooltip) && !targetType.suppressDisabledTooltip ? targetType.strings.disabledTooltip : undefined
            };
        });

        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                <OEStdCapBarSelector
                    className={this.props.className}
                    moduleId={this.props.moduleId}
                    disabled={this.props.disabled}
                    showMainMenuControlBtn={this.props.showMainMenuControlBtn}
                    showFullscreenBtn={this.props.showFullscreenBtn}
                    isFullscreen={this.props.isFullscreen}
                    appComponent={this.props.appComponent}
                    targetBarTitle={targetBarTitle}
                    targetBarLabel={this.props.targetBarLabel}
                    targetBarButton={targetBarButton}
                    targetTypeBar={targetTypeBar}
                    targetMenuEntries={targetMenuEntries}
                    targetMenuDisabled={targetMenuDisabled}
                    targetMenuHidden={targetMenuHidden}
                    onTargetMenuDisabledToggle={onTargetMenuDisabledToggle}
                    leftMainMenuButtons={this.props.leftMainMenuButtons}
                    rightMainMenuButtons={this.props.rightMainMenuButtons}
                />
            </React.Fragment>
        );
    }

    onTargetBtnPressed(event, sender)    {
        let props = this.props;

        if(props.onTargetBtnPressed)   {
            props.onTargetBtnPressed(sender.props.userData, props.target, this);
            return;
        }

        if(!props.appComponent)    return;

        let userData = sender.props.userData;

        // start selection for target
        if(OEFNStartSelectionContentFactory.isContent(userData.startSelection) && props.appComponent.uiLayer && props.appComponent.uiLayer.startSelectionView) {
           
            if(this.props.noStartSelectionForActiveTargetType && userData.id === this.state.activeTargetType)    return;

            let content;

            if(Array.isArray(userData.startSelection)) {                                                // module or category array

                content = OEFNStartSelectionContentFactory.areCategories(userData.startSelection) ? {id: userData.id, isHidden: true, categories: userData.startSelection} : userData.startSelection;

            } else if(OEFNStartSelectionContentFactory.isModuleOrCategory(entry.startSelection)) {      // module or category item

                content = OEFNStartSelectionContentFactory.isCategory(entry.startSelection) ? Object.assign({id: userData.id}, userData.startSelection) : userData.startSelection;
            }

            props.appComponent.uiLayer.startSelectionView.setOpen(true, {content: content});
            return;
        }

        // direct target switch
        let lastTargetForType = this.getLastTargetForType(userData.id);

        let target = lastTargetForType ? lastTargetForType : userData.target;
        if(props.targetTypeTargetForTarget)    {
            let target_ = props.targetTypeTargetForTarget(userData, props.target, this, Object.assign({}, this.licensingAccess, {oe: this.oe}));
            if(typeof(target_) === 'string')    target = target_;
        }
        
        if(typeof(target) !== 'string') return;
        props.appComponent.app.changeTarget(target);
    }

    onTargetBtnDisabledPressed(event, sender)   {
        if(this.props.onTargetBtnDisabledPressed)   this.props.onTargetBtnDisabledPressed(sender.props.userData, this.props.target, this);
    }
}

OEStdCapBarController.defaultProps = {
    className:                      '',
    moduleId:                       '',
    target:                         '',
    showMainMenuControlBtn:         true,
    showFullscreenBtn:              true,
    isFullscreen:                   false,
    targetTypes: [
        {id: OETarget.humanEyeMacro, target: OETarget.humanEyeMacro, icon: OEIconCodes.zeissOpti.anatomyTarget, titleId: 'target_menu_anatomy'},
        {id: OETarget.zeissOpti2, target: OETarget.zeissOpti2, icon: OEIconCodes.zeissOpti.optiTarget, titleId: 'target_menu_zeissOpti2'},
        {id: OETarget.zeissUV, target: OETarget.zeissUV, icon: OEIconCodes.zeissOpti.UVTarget, titleId: 'target_menu_zeissUV'}
    ],
    noStartSelectionForActiveTargetType:    false
};

OEStdCapBarController.propTypes = {
    className:                      PropTypes.string,
    moduleId:                       PropTypes.string,
    disabled:                       PropTypes.bool,
    target:                         PropTypes.string,
    showMainMenuControlBtn:         PropTypes.bool,
    showFullscreenBtn:              PropTypes.bool,
    isFullscreen:                   PropTypes.bool,
    appComponent:                   PropTypes.object,
    targetTypes:                    OEStdCapBarPropTypes.targetTypes,
    noStartSelectionForActiveTargetType: PropTypes.bool,
    targetBarLabel:                 PropTypes.string,
    leftMainMenuButtons:            OEMainMenuButtonsPropType,
    rightMainMenuButtons:           OEMainMenuButtonsPropType,
    targetTypeForTarget:            PropTypes.func,
    isTargetTypeDisabled:           PropTypes.func,
    onTargetBtnPressed:             PropTypes.func,
    onTargetBtnDisabledPressed:     PropTypes.func,
    targetTypeTargetForTarget:      PropTypes.func
};

//
export default frefFromRef(connectAppEnv((env) => {
    return {
        appComponent:               env.component,
        target:                     env.config.target,
        showMainMenuControlBtn:     !env.config.module.uiLayerConfig.mainMenuConfig.hovering,
        showFullscreenBtn:          env.state.showFullscreenBtn,
        isFullscreen:               env.state.isFullscreen,
        mainMenuButtons:            env.config.module.uiLayerConfig.mainMenuButtons
    };
})(withAddedUILevel(translateProps(props => {
    if(props.config)    {
        props.showMainMenuControlBtn = props.showMainMenuControlBtn && !!props.config.mainMenuBtn;
        props.showFullscreenBtn = props.showFullscreenBtn && !!props.config.fullscreenBtn;
    }
    
    if(props.mainMenuButtons)   {
        let mainMenuButtons = props.mainMenuButtons;    // here mainMenuButtons are translated with respect to uiLevel
        delete props.mainMenuButtons;
        props.leftMainMenuButtons = mainMenuButtons.left;
        props.rightMainMenuButtons = mainMenuButtons.right;
    }

    return props;
})(frefToRef(OEStdCapBarController)))));